/**
 * Аудит (§51).
 *
 * Всё, что меняет чужие данные или деньги, оставляет след: кто, что, до и после.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AdminAuditRow } from '@alterium/shared';
import { adminApi } from '../../api/admin';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { formatDateTime } from '../../i18n/format';
import { useTranslation } from 'react-i18next';

export function AdminAuditPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');

  const audit = useQuery({
    queryKey: ['admin', 'audit', page, query],
    queryFn: () => adminApi.audit({ page, query }),
  });

  const columns: Array<Column<AdminAuditRow>> = [
    {
      key: 'time',
      title: t('admin.audit.when'),
      render: (row) => formatDateTime(row.createdAt),
      width: '150px',
    },
    { key: 'actor', title: t('admin.audit.who'), render: (row) => row.actorEmail ?? t('admin.audit.system') },
    { key: 'action', title: t('admin.audit.action'), render: (row) => row.action },
    { key: 'entity', title: t('admin.audit.entity'), render: (row) => `${row.entity} ${row.entityId ?? ''}` },
    {
      // На общем аккаунте почта одна на всех: кто именно — видно по адресу.
      key: 'ip',
      title: t('admin.audit.from'),
      render: (row) => row.ip ?? '—',
      width: '130px',
    },
    {
      key: 'meta',
      title: t('admin.audit.details'),
      render: (row) => (
        <code className="text-[11px] text-content-secondary">
          {JSON.stringify(row.meta)?.slice(0, 160) ?? '—'}
        </code>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <input
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setPage(1);
        }}
        placeholder={t('admin.audit.searchPlaceholder')}
        className="h-9 w-64 rounded-control border border-line bg-surface px-3 text-meta text-content placeholder:text-content-muted focus:border-accent focus:outline-none"
      />

      <DataTable
        columns={columns}
        rows={audit.data?.items ?? []}
        rowKey={(row) => row.id}
        total={audit.data?.total}
        page={page}
        totalPages={audit.data?.totalPages}
        loading={audit.isLoading}
        onPage={setPage}
      />
    </div>
  );
}
